import * as amplitude from "@amplitude/analytics-browser";
import {
  Button,
  Flex,
  FormErrorMessage,
  Icon,
  LinkBox,
  LinkOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useField } from "formik";
import { FunctionComponent } from "react";
import { RiArrowRightSLine } from "react-icons/ri";
import { Card } from "src/components/Layout/Card";
import { RouterLink } from "src/components/Links/RouterLink";
import {
  CustomQuestionAnswer,
  CustomQuestionAnswersByQuestionId,
} from "src/schemas/CustomQuestionType";
import { answerIsEmpty } from "src/services/formTemplate/answer";
import * as AF from "src/types/formTemplate";
import { OutdatedAnswerAlert } from "./form/OutdatedAnswerAlert";

type ParentGuardianCustomQuestionProps = {
  answerCustomQuestionRoute: string;
  formId: uuid;
  questionId: uuid;
  customQuestionTypeName: string;
  nestedQuestions: AF.ClonedQuestion<AF.WithId>[];
  answersByQuestionId: CustomQuestionAnswersByQuestionId;
};

export const ParentGuardianCustomQuestion: FunctionComponent<
  ParentGuardianCustomQuestionProps
> = (props) => {
  const {
    answerCustomQuestionRoute,
    formId,
    questionId,
    customQuestionTypeName,
    nestedQuestions,
    answersByQuestionId,
  } = props;

  const { isOpen: outdatedAlertIsOpen, onClose: outdatedAlertOnClose } =
    useDisclosure({
      defaultIsOpen: true,
    });

  const questionHasAnswer = !answerIsEmpty(answersByQuestionId);
  const answersList = getAnswersList({ nestedQuestions, answersByQuestionId });
  const [, { error }] = useField<CustomQuestionAnswer | undefined>(questionId);

  return (
    <Flex gap={2} direction="column">
      {outdatedAlertIsOpen && (
        <OutdatedAnswerAlert
          formId={formId}
          questionId={questionId}
          nestedQuestions={nestedQuestions}
          closeAlert={outdatedAlertOnClose}
          refetchAnswers
        />
      )}
      {questionHasAnswer && (
        <>
          <LinkBox
            as={Card}
            showBorder
            _hover={{ backgroundColor: "blackAlpha.100" }}
            borderColor={error ? "red.500" : undefined}
            boxShadow={
              error ? "0 0 0 1px var(--chakra-colors-red-500)" : undefined
            }
          >
            <LinkOverlay
              as={RouterLink}
              to={answerCustomQuestionRoute}
              _hover={{ textDecoration: "none" }}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Flex direction="column">
                  {answersList.map((answer) => {
                    return (
                      <Text key={answer} fontSize="md">
                        {answer}
                      </Text>
                    );
                  })}
                </Flex>

                <Icon boxSize={4} as={RiArrowRightSLine} />
              </Flex>
            </LinkOverlay>
          </LinkBox>
          {error && (
            <FormErrorMessage marginTop={0}>
              Selection is missing required fields or invalid
            </FormErrorMessage>
          )}
        </>
      )}
      <Button
        as={RouterLink}
        to={answerCustomQuestionRoute}
        size="sm"
        variant="outline"
        colorScheme="gray"
        width="100%"
        _hover={{
          textDecoration: "none",
          backgroundColor: "gray.100",
        }}
        onClick={() => {
          if (questionHasAnswer) {
            amplitude.track("Select different CQT entry clicked", {
              cqt_name: customQuestionTypeName,
            });
          } else {
            amplitude.track("Add CQT to form clicked", {
              cqt_name: customQuestionTypeName,
            });
          }
        }}
      >
        {questionHasAnswer
          ? `Select different ${customQuestionTypeName.toLocaleLowerCase()}`
          : `Add ${customQuestionTypeName.toLocaleLowerCase()}`}
      </Button>
    </Flex>
  );
};

const NUMBER_OF_ANSWERS_TO_DISPLAY = 2;
const CONTACT_FIELDS_TO_DISPLAY = ["first_name", "last_name", "dob"];

function getAnswersList(
  props: Pick<
    ParentGuardianCustomQuestionProps,
    "nestedQuestions" | "answersByQuestionId"
  >
): string[] {
  const { nestedQuestions, answersByQuestionId } = props;

  // ------------------------------------------------------------
  // special cased logic for SAISD based on the CQT field
  const contactFields = nestedQuestions.filter((question) => {
    return CONTACT_FIELDS_TO_DISPLAY.includes(question.key ?? "");
  });
  const questionIsContact =
    contactFields.length === CONTACT_FIELDS_TO_DISPLAY.length;
  if (questionIsContact) {
    return [
      contactFields
        .slice(0, 2)
        .map((field) => {
          return answersByQuestionId[field.id] ?? "";
        })
        .join(" "),
      contactFields
        .slice(2)
        .map((field) => {
          return answersByQuestionId[field.id] ?? "";
        })
        .join(""),
    ];
  }
  // ------------------------------------------------------------

  return nestedQuestions
    .slice(0, NUMBER_OF_ANSWERS_TO_DISPLAY)
    .map((currentQuestion) => {
      const { id, type } = currentQuestion;
      const answerValue = answersByQuestionId[id] ?? "";
      const answerText =
        type === AF.SingleSelectType
          ? currentQuestion.options.find((option) => option.id === answerValue)
              ?.label ?? ""
          : answerValue;
      return answerText;
    });
}
